import { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';

// Player de audio do aplicativo do cliente.
//
// ⚠️ **Nao reusa o `ReprodutorAudio` do ERP de proposito**, pelo mesmo motivo que o
// `GravadorVoz` nao reusa o gravador da equipe: aquele e' uma funcao local dentro do
// `MensagemBubble.tsx` (nao exportada), toda em Tailwind calibrado pro balao da
// vendedora, e arrasta o arquivo inteiro junto — `emoji-picker-react`, lightbox,
// miniatura de PDF. Aqui a tela e' de cliente no celular: estilo inline, paleta
// propria e alvo de toque de 44px.
//
// A preferencia de velocidade tambem tem chave SEPARADA da do ERP
// (`fdc.audio.velocidade`): localStorage e' por origem, e as duas telas moram no
// mesmo dominio — compartilhar a chave faria o cliente mudar a velocidade da
// vendedora e vice-versa.

const VELOCIDADES = [1, 1.5, 2];
const CHAVE_VELOCIDADE = 'fiodecobre.app.audio.velocidade';

function rotuloVelocidade(v: number): string {
  return `${String(v).replace('.', ',')}x`;
}

function mmss(segundos: number): string {
  if (!Number.isFinite(segundos) || segundos < 0) return '0:00';
  const m = Math.floor(segundos / 60);
  const s = Math.floor(segundos % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function PlayerAudio({ src, cor }: { src: string; cor: string }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const trilhoRef = useRef<HTMLDivElement | null>(null);
  const [tocando, setTocando] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(0);
  const [velocidade, setVelocidade] = useState<number>(() => {
    const guardada = Number(localStorage.getItem(CHAVE_VELOCIDADE));
    return VELOCIDADES.includes(guardada) ? guardada : 1;
  });

  // `playbackRate` se perde a cada troca de fonte e em alguns navegadores ao dar
  // play — reaplicar e' mais barato do que descobrir onde ele zerou.
  useEffect(() => {
    if (ref.current) ref.current.playbackRate = velocidade;
  }, [velocidade, tocando]);

  function ciclarVelocidade() {
    const i = VELOCIDADES.indexOf(velocidade);
    const nova = VELOCIDADES[(i + 1) % VELOCIDADES.length]!;
    setVelocidade(nova);
    localStorage.setItem(CHAVE_VELOCIDADE, String(nova));
  }

  // ⚠️ Audio gravado pelo MediaRecorder (webm/opus) nasce **sem duracao no
  // cabecalho** — o `duration` vem `Infinity` ate alguem procurar o fim do arquivo.
  // Sem esta manobra o tempo total fica em 0:00 e a barra nunca anda (e' o que a
  // tela do ERP mostra hoje nos audios do app). Buscar um instante absurdo forca o
  // navegador a varrer e responder a duracao real; depois volta pro comeco.
  function aoCarregarMetadados() {
    const el = ref.current;
    if (!el) return;
    if (Number.isFinite(el.duration)) { setDur(el.duration); return; }
    const aoAndar = () => {
      if (!Number.isFinite(el.duration)) return;
      el.removeEventListener('timeupdate', aoAndar);
      setDur(el.duration);
      el.currentTime = 0;
      setPos(0);
    };
    el.addEventListener('timeupdate', aoAndar);
    el.currentTime = 1e101;
  }

  function alternar() {
    const el = ref.current;
    if (!el) return;
    if (el.paused) { el.playbackRate = velocidade; void el.play(); } else el.pause();
  }

  function irPara(clienteX: number) {
    const trilho = trilhoRef.current;
    const el = ref.current;
    if (!trilho || !el || !dur) return;
    const r = trilho.getBoundingClientRect();
    const fracao = Math.min(1, Math.max(0, (clienteX - r.left) / r.width));
    el.currentTime = fracao * dur;
    setPos(el.currentTime);
  }

  const progresso = dur > 0 ? Math.min(100, (pos / dur) * 100) : 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 210 }}>
      <button
        type="button"
        onClick={alternar}
        aria-label={tocando ? 'Pausar' : 'Tocar'}
        style={{
          width: 38, height: 38, flexShrink: 0, borderRadius: 999, backgroundColor: cor, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {tocando ? <Pause size={17} /> : <Play size={17} style={{ marginLeft: 2 }} />}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          ref={trilhoRef}
          onClick={(e) => irPara(e.clientX)}
          // Alvo de toque generoso: a barra em si tem 4px, mas o dedo acerta 16px.
          style={{ padding: '6px 0', cursor: 'pointer' }}
        >
          <div style={{ height: 4, borderRadius: 999, backgroundColor: 'rgba(0,0,0,0.14)', position: 'relative' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${progresso}%`, backgroundColor: cor, borderRadius: 999 }} />
            <div
              style={{
                position: 'absolute', top: -4, left: `${progresso}%`, marginLeft: -6,
                width: 12, height: 12, borderRadius: 999, backgroundColor: cor,
              }}
            />
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#78716c', marginTop: -2 }}>
          {mmss(pos)} / {mmss(dur)}
        </div>
      </div>

      <button
        type="button"
        onClick={ciclarVelocidade}
        aria-label={`Velocidade ${rotuloVelocidade(velocidade)}, tocar para mudar`}
        style={{
          flexShrink: 0, minWidth: 44, height: 30, borderRadius: 999,
          border: `1px solid ${cor}`, color: cor, backgroundColor: 'transparent',
          fontSize: 12, fontWeight: 700, letterSpacing: 0.2,
        }}
      >
        {rotuloVelocidade(velocidade)}
      </button>

      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onLoadedMetadata={aoCarregarMetadados}
        onDurationChange={() => { const d = ref.current?.duration; if (d && Number.isFinite(d)) setDur(d); }}
        onTimeUpdate={() => setPos(ref.current?.currentTime ?? 0)}
        onPlay={() => setTocando(true)}
        onPause={() => setTocando(false)}
        onEnded={() => { setTocando(false); setPos(0); if (ref.current) ref.current.currentTime = 0; }}
        style={{ display: 'none' }}
      />
    </div>
  );
}
